<template>
    <b-modal ref="modalRef" id="engEffPointConfig" size="lg" title="관제점 설정" @hidden="$emit('close')">
        <div v-if="loading" class="engeff-config-loading">
            <i class="fas fa-spinner fa-spin"></i> 불러오는 중...
        </div>
        <div v-else>
            <div class="engeff-config-description">
                <i class="fas fa-info-circle"></i>
                <span>에너지 운영 보고서 각 지표가 참조하는 관제점을 슬롯별로 설정합니다.</span>
            </div>
            <div v-if="loadError" class="engeff-config-error">{{ loadError }}</div>

            <div class="engeff-config-list">
                <div v-for="group in groupedConfigs" :key="group.category" class="engeff-config-group">
                    <div class="engeff-config-group-header">
                        <i :class="group.icon"></i>
                        <span>{{ group.category }}</span>
                    </div>

                    <div
                        v-for="cfg in group.items"
                        :key="cfg.configIdx"
                        class="engeff-config-item"
                        :class="{ incomplete: hasEmptySlot(cfg) }"
                    >
                        <div class="engeff-config-item-header">
                            <span class="engeff-config-label">{{ cfg.metricLabel }}</span>
                            <span class="engeff-config-mode-badge" :class="cfg.selectMode.toLowerCase()">
                                {{ cfg.selectMode === "MULTI" ? "그룹형" : "단일" }}
                            </span>
                        </div>

                        <div class="engeff-config-slots">
                            <div v-for="slot in cfg.slots" :key="slot.roleCode || '_'" class="engeff-config-slot">
                                <span v-if="slot.roleCode" class="engeff-config-role">{{
                                    roleLabel(slot.roleCode)
                                }}</span>
                                <span class="engeff-config-point" :class="{ empty: !slot.ptIdx }">
                                    <template v-if="slot.ptIdx">
                                        {{ slot.ptName }}
                                        <span class="engeff-config-point-addr">{{ slot.ptAddr }}</span>
                                    </template>
                                    <template v-else>
                                        <i class="fas fa-exclamation-triangle"></i> 미설정 — 이 지표는 리포트에 반영되지 않습니다
                                    </template>
                                </span>
                                <button
                                    type="button"
                                    class="btn btn-sm btn-outline-secondary engeff-config-btn"
                                    @click="openPicker(cfg, slot)"
                                >
                                    {{ slot.ptIdx ? "변경" : "설정" }}
                                </button>
                            </div>
                        </div>

                        <div v-if="cfg.selectMode === 'MULTI'" class="engeff-config-multi-hint">
                            <i class="fas fa-info-circle"></i>
                            이 카드의 슬롯 전체가 [저장] 한 번에 함께 적용됩니다.
                        </div>

                        <div class="engeff-config-item-footer">
                            <button
                                type="button"
                                class="btn btn-sm btn-primary engeff-config-btn"
                                :disabled="savingConfigIdx === cfg.configIdx || !isDirty(cfg)"
                                @click="saveConfig(cfg)"
                            >
                                <i v-if="savingConfigIdx === cfg.configIdx" class="fas fa-spinner fa-spin"></i>
                                저장
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <PointSearchModal
            :visible.sync="pickerVisible"
            :keyword.sync="pickerKeyword"
            :loading="false"
            :rows="filteredPoints"
            :list-total-count="allPoints.length"
            title="관제점 선택"
            :subtitle="pickerSubtitle"
            @search="() => {}"
            @select="onPickPoint"
            @close="pickerVisible = false"
        />

        <template #modal-footer>
            <div class="engeff-config-modal-footer">
                <button type="button" class="btn btn-secondary" @click="close">닫기</button>
            </div>
        </template>
    </b-modal>
</template>

<script>
import backEndApi from "@src/api/backEndApi";
import PointSearchModal from "@src/views/production-unit-config/components/PointSearchModal.vue";

const ROLE_LABELS = {
    ELEC: "전기",
    GAS: "가스",
    PV: "태양광",
    GT: "지열",
    FC: "연료전지",
};

/** MULTI 슬롯이 항상 표시할 하위 역할 목록 (매핑이 비어있어도 슬롯 자체는 보여준다) */
const ROLE_CATALOG = {
    SOURCE_CSTN_ELEC_GAS: ["ELEC", "GAS"],
    SOURCE_ENSPLY_ELEC_GAS: ["ELEC", "GAS"],
    REN_PROD: ["PV", "GT", "FC"],
};

/** 카드 스캔성을 위한 카테고리 분류 (화면 표시 전용 — 서버 데이터에는 없는 그룹핑) */
const CATEGORY_CATALOG = {
    TOTAL_CSTN: "에너지 소비·공급",
    TOTAL_ENSPLY: "에너지 소비·공급",
    SOURCE_CSTN_ELEC_GAS: "에너지 소비·공급",
    SOURCE_ENSPLY_ELEC_GAS: "에너지 소비·공급",
    REN_PROD: "신재생·자립률",
    ENSPLY_PV: "신재생·자립률",
    SELF_RATE: "신재생·자립률",
    ELEC_COST: "비용",
    GAS_COST: "비용",
    ENSPLY_GT: "비용",
    FLOOR_AREA: "건물 정보",
    CSTN_GOAL: "건물 정보",
    OUTER_TEMP: "운영 환경",
    OUTER_HUMI: "운영 환경",
};
const CATEGORY_ORDER = ["에너지 소비·공급", "신재생·자립률", "비용", "건물 정보", "운영 환경"];
const CATEGORY_ICONS = {
    "에너지 소비·공급": "fas fa-bolt",
    "신재생·자립률": "fas fa-leaf",
    비용: "fa fa-calculator",
    "건물 정보": "fas fa-building",
    "운영 환경": "fas fa-thermometer-half",
    기타: "fas fa-list",
};

export default {
    name: "EngEffPointConfigModal",
    components: { PointSearchModal },
    data() {
        return {
            loading: false,
            loadError: "",
            configs: [],
            allPoints: [],
            pickerVisible: false,
            pickerKeyword: "",
            activeSlot: null,
            savingConfigIdx: null,
        };
    },
    computed: {
        pickerSubtitle() {
            return this.activeSlot?.roleCode ? this.roleLabel(this.activeSlot.roleCode) : "";
        },
        filteredPoints() {
            const kw = (this.pickerKeyword || "").trim().toLowerCase();
            if (!kw) return this.allPoints;
            return this.allPoints.filter(
                (p) =>
                    String(p.ptAddr || "").toLowerCase().includes(kw) ||
                    String(p.ptName || "").toLowerCase().includes(kw),
            );
        },
        /** 카테고리별로 묶고, 각 카테고리 안에서는 미설정 슬롯이 있는 카드를 위로 정렬 */
        groupedConfigs() {
            const buckets = new Map();
            CATEGORY_ORDER.forEach((cat) => buckets.set(cat, []));

            this.configs.forEach((cfg) => {
                const cat = CATEGORY_CATALOG[cfg.metricKey] || "기타";
                if (!buckets.has(cat)) buckets.set(cat, []);
                buckets.get(cat).push(cfg);
            });

            buckets.forEach((list) => {
                list.sort((a, b) => Number(this.hasEmptySlot(b)) - Number(this.hasEmptySlot(a)));
            });

            return Array.from(buckets.entries())
                .filter(([, list]) => list.length > 0)
                .map(([category, items]) => ({
                    category,
                    icon: CATEGORY_ICONS[category] || CATEGORY_ICONS["기타"],
                    items,
                }));
        },
    },
    methods: {
        show() {
            this.$refs.modalRef.show();
            this.loadAll();
        },
        close() {
            this.$refs.modalRef.hide();
        },
        roleLabel(roleCode) {
            return ROLE_LABELS[roleCode] || roleCode;
        },
        hasEmptySlot(cfg) {
            return cfg.slots.some((s) => !s.ptIdx);
        },
        /** 슬롯 배열에서 비교에 필요한 값만 스냅샷 (저장 여부 판단용) */
        snapshotSlots(slots) {
            return slots.map((s) => ({ roleCode: s.roleCode, ptIdx: s.ptIdx }));
        },
        /** 마지막 저장(또는 최초 로드) 시점 대비 슬롯이 바뀌었는지 */
        isDirty(cfg) {
            if (!cfg.originalSlots) return false;
            return cfg.slots.some((s, i) => (cfg.originalSlots[i]?.ptIdx ?? null) !== (s.ptIdx ?? null));
        },
        async loadAll() {
            this.loading = true;
            this.loadError = "";
            try {
                const [configRes, pointRes] = await Promise.all([
                    backEndApi.engEfficiencyConfig.getConfigList(),
                    backEndApi.pointInfo.getPointList(),
                ]);

                this.allPoints = Array.isArray(pointRes?.data) ? pointRes.data : [];

                const rows = Array.isArray(configRes?.data?.rows) ? configRes.data.rows : [];
                this.configs = rows.map((cfg) => {
                    const slots = this.buildSlots(cfg);
                    return {
                        ...cfg,
                        slots,
                        originalSlots: this.snapshotSlots(slots),
                    };
                });
            } catch (err) {
                console.error("engEfficiencyConfig getConfigList 에러:", err);
                this.loadError = "관제점 설정을 불러오지 못했습니다.";
            } finally {
                this.loading = false;
            }
        },
        buildSlots(cfg) {
            const sourcesByRole = new Map();
            (cfg.sources || []).forEach((s) => sourcesByRole.set(s.roleCode || null, s));

            const roleCodes = ROLE_CATALOG[cfg.metricKey] || (cfg.selectMode === "MULTI" ? [] : [null]);

            return roleCodes.map((roleCode) => {
                const matched = sourcesByRole.get(roleCode);
                return {
                    roleCode,
                    ptIdx: matched ? matched.ptIdx : null,
                    ptAddr: matched ? matched.ptAddr : null,
                    ptName: matched ? matched.ptName : null,
                };
            });
        },
        openPicker(cfg, slot) {
            this.activeConfig = cfg;
            this.activeSlot = slot;
            this.pickerKeyword = "";
            this.pickerVisible = true;
        },
        onPickPoint(point) {
            if (!this.activeSlot || !point) return;
            this.activeSlot.ptIdx = point.ptIdx;
            this.activeSlot.ptAddr = point.ptAddr;
            this.activeSlot.ptName = point.ptName;
            this.pickerVisible = false;
        },
        async saveConfig(cfg) {
            const items = cfg.slots
                .filter((s) => s.ptIdx)
                .map((s) => ({ ptIdx: s.ptIdx, roleCode: s.roleCode || undefined }));

            if (items.length === 0) {
                this.alertWarning("관제점을 1개 이상 선택해 주세요.");
                return;
            }

            this.savingConfigIdx = cfg.configIdx;
            try {
                await backEndApi.engEfficiencyConfig.saveSource({ configIdx: cfg.configIdx, items });
                cfg.originalSlots = this.snapshotSlots(cfg.slots);
                this.alertNoti(`${cfg.metricLabel} 설정이 저장되었습니다.`, "관제점 설정");
                this.$emit("saved", cfg.metricKey);
            } catch (err) {
                console.error("engEfficiencyConfig saveSource 에러:", err);
                this.alertWarning(err?.response?.data?.message || "저장 중 오류가 발생했습니다.");
            } finally {
                this.savingConfigIdx = null;
            }
        },
    },
};
</script>

<style scoped>
.engeff-config-loading {
    text-align: center;
    padding: 2rem 0;
    color: #6c757d;
}
.engeff-config-description {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    color: #495057;
    font-size: 0.85rem;
    margin-bottom: 0.75rem;
}
.engeff-config-error {
    color: #dc3545;
    font-size: 0.85rem;
    margin-bottom: 0.75rem;
}

/* 목록 / 그룹 */
.engeff-config-list {
    max-height: 60vh;
    overflow-y: auto;
    padding-right: 0.25rem;
}
.engeff-config-group + .engeff-config-group {
    margin-top: 1.25rem;
}
.engeff-config-group-header {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    font-size: 0.78rem;
    font-weight: 700;
    color: #6c757d;
    text-transform: uppercase;
    letter-spacing: 0.02em;
    padding-bottom: 0.35rem;
    margin-bottom: 0.6rem;
    border-bottom: 1px solid #e9ecef;
}
.engeff-config-group-header i {
    color: #868e96;
    width: 1rem;
    text-align: center;
}

/* 카드 */
.engeff-config-item {
    border: 1px solid #e9ecef;
    border-radius: 8px;
    padding: 0.75rem 1rem;
    margin-bottom: 0.6rem;
}
.engeff-config-item.incomplete {
    border-color: #fde68a;
    background: #fffdf6;
}
.engeff-config-item-header {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 0.5rem;
}
.engeff-config-label {
    font-weight: 700;
    font-size: 0.9rem;
}
.engeff-config-mode-badge {
    font-size: 0.65rem;
    font-weight: 600;
    padding: 0.05rem 0.45rem;
    border-radius: 10px;
    background: transparent;
    color: #adb5bd;
    border: 1px solid #dee2e6;
}
.engeff-config-mode-badge.multi {
    color: #4c51bf;
    border-color: #c7d2fe;
}

/* 슬롯 */
.engeff-config-slots {
    display: flex;
    flex-direction: column;
    gap: 0.4rem;
}
.engeff-config-slot {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.85rem;
}
.engeff-config-role {
    min-width: 3.5rem;
    color: #6c757d;
    font-weight: 600;
}
.engeff-config-point {
    flex: 1 1 auto;
    line-height: 1.3;
}
.engeff-config-point-addr {
    display: block;
    font-size: 0.72rem;
    color: #adb5bd;
    font-family: "SFMono-Regular", Consolas, monospace;
    margin-top: 0.1rem;
}
.engeff-config-point.empty {
    color: #b45309;
    font-weight: 600;
    display: flex;
    align-items: center;
    gap: 0.35rem;
}
.engeff-config-point.empty i {
    color: #f59e0b;
}

/* 그룹형 안내 */
.engeff-config-multi-hint {
    display: flex;
    align-items: center;
    gap: 0.3rem;
    font-size: 0.75rem;
    color: #868e96;
    margin-top: 0.5rem;
}

/* 버튼 */
.engeff-config-btn {
    min-width: 4.5rem;
    text-align: center;
}
.engeff-config-item-footer {
    display: flex;
    justify-content: flex-end;
    margin-top: 0.6rem;
}
.engeff-config-item-footer .btn:disabled {
    cursor: not-allowed;
}

.engeff-config-modal-footer {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    width: 100%;
}
</style>
